import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { useLocalStorage } from '@/hooks/use-local-storage';

export interface TourStep {
  id: string;
  title: string;
  description: string;
  targetSelector: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  videoUrl?: string;
  imageUrl?: string;
}

interface TourState {
  isActive: boolean;
  currentStep: number;
  isDismissed: boolean;
  isCompleted: boolean;
}

interface TourAnalytics {
  startedAt?: string;
  completedAt?: string;
  stepsViewed: number[];
  timeSpentPerStep: Record<number, number>;
  skippedAt?: number;
}

interface TourContextValue {
  tourState: TourState;
  steps: TourStep[];
  analytics: TourAnalytics;
  startTour: () => void;
  nextStep: () => void;
  previousStep: () => void;
  skipTour: () => void;
  finishTour: () => void;
  goToStep: (stepIndex: number) => void;
  showFeedbackModal: boolean;
  setShowFeedbackModal: (show: boolean) => void;
}

const TourContext = createContext<TourContextValue | undefined>(undefined);

const TOUR_STEPS: TourStep[] = [
  {
    id: 'navigation',
    title: 'Navigation Menu',
    description: 'Use the navigation menu to move between different sections of Resource Scheduler. You\'re currently viewing the Calendar.',
    targetSelector: '[data-tour="navigation"]',
    position: 'bottom',
  },
  {
    id: 'calendar-grid',
    title: 'Calendar View',
    description: 'This is your main calendar view where you can see all resource bookings across your team. Resources are listed on the left, with their schedules displayed across the timeline.',
    targetSelector: '.calendar-grid-container',
    position: 'top',
  },
  {
    id: 'calendar-view',
    title: 'Change Calendar View',
    description: 'Switch between different calendar views (Day, Week, Month, Quarter, Year) using this dropdown. Choose the view that works best for your planning needs.',
    targetSelector: '[data-tour="calendar-view"]',
    position: 'bottom',
  },
  {
    id: 'create-booking',
    title: 'Create a Booking',
    description: 'Click here to create a new booking for a resource. You\'ll be able to select the resource, project, time period, and other details.',
    targetSelector: '[data-tour="create-booking"]',
    position: 'left',
  },
  {
    id: 'edit-booking',
    title: 'Edit a Booking',
    description: 'Right-click on any booking to edit its details, or drag and drop it to move it to a different time. You can also drag the edges to extend or shorten the booking.',
    targetSelector: '.booking-bar:first-of-type',
    position: 'top',
  },
];

interface TourProviderProps {
  children: ReactNode;
}

export function TourProvider({ children }: TourProviderProps) {
  const [tourDismissed, setTourDismissed] = useLocalStorage('tourDismissed', false);
  const [tourCompleted, setTourCompleted] = useLocalStorage('tourCompleted', false);
  const [savedStep, setSavedStep] = useLocalStorage('tourCurrentStep', 0);
  const [tourAnalytics, setTourAnalytics] = useLocalStorage<TourAnalytics>('tourAnalytics', {
    stepsViewed: [],
    timeSpentPerStep: {},
  });
  
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(savedStep);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [stepStartTime, setStepStartTime] = useState<number>(Date.now());

  const startTour = useCallback(() => {
    setIsActive(true);
    setCurrentStep(0);
    setSavedStep(0);
    setTourDismissed(false);
    setTourCompleted(false);
    setStepStartTime(Date.now());
    
    // Track tour start
    setTourAnalytics({
      startedAt: new Date().toISOString(),
      stepsViewed: [0],
      timeSpentPerStep: {},
    });
  }, [setSavedStep, setTourDismissed, setTourCompleted, setTourAnalytics]);

  const nextStep = useCallback(() => {
    if (currentStep < TOUR_STEPS.length - 1) {
      // Track time spent on current step
      const timeSpent = Date.now() - stepStartTime;
      setTourAnalytics(prev => ({
        ...prev,
        timeSpentPerStep: {
          ...prev.timeSpentPerStep,
          [currentStep]: timeSpent,
        },
        stepsViewed: [...new Set([...prev.stepsViewed, currentStep + 1])],
      }));
      
      const nextStepIndex = currentStep + 1;
      setCurrentStep(nextStepIndex);
      setSavedStep(nextStepIndex);
      setStepStartTime(Date.now());
    }
  }, [currentStep, setSavedStep, stepStartTime, setTourAnalytics]);

  const previousStep = useCallback(() => {
    if (currentStep > 0) {
      // Track time spent on current step
      const timeSpent = Date.now() - stepStartTime;
      setTourAnalytics(prev => ({
        ...prev,
        timeSpentPerStep: {
          ...prev.timeSpentPerStep,
          [currentStep]: (prev.timeSpentPerStep[currentStep] || 0) + timeSpent,
        },
      }));
      
      const prevStepIndex = currentStep - 1;
      setCurrentStep(prevStepIndex);
      setSavedStep(prevStepIndex);
      setStepStartTime(Date.now());
    }
  }, [currentStep, setSavedStep, stepStartTime, setTourAnalytics]);

  const skipTour = useCallback(() => {
    // Track skip analytics
    setTourAnalytics(prev => ({
      ...prev,
      skippedAt: currentStep,
    }));
    
    setIsActive(false);
    setTourDismissed(true);
    setSavedStep(0);
  }, [setTourDismissed, setSavedStep, currentStep, setTourAnalytics]);

  const finishTour = useCallback(() => {
    // Track completion analytics
    const timeSpent = Date.now() - stepStartTime;
    setTourAnalytics(prev => ({
      ...prev,
      completedAt: new Date().toISOString(),
      timeSpentPerStep: {
        ...prev.timeSpentPerStep,
        [currentStep]: timeSpent,
      },
    }));
    
    setIsActive(false);
    setTourCompleted(true);
    setTourDismissed(true);
    setSavedStep(0);
    
    // Show feedback modal after tour completion
    setTimeout(() => {
      setShowFeedbackModal(true);
    }, 500);
  }, [setTourCompleted, setTourDismissed, setSavedStep, currentStep, stepStartTime, setTourAnalytics]);

  const goToStep = useCallback((stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < TOUR_STEPS.length) {
      // Track time spent on current step
      const timeSpent = Date.now() - stepStartTime;
      setTourAnalytics(prev => ({
        ...prev,
        timeSpentPerStep: {
          ...prev.timeSpentPerStep,
          [currentStep]: (prev.timeSpentPerStep[currentStep] || 0) + timeSpent,
        },
        stepsViewed: [...new Set([...prev.stepsViewed, stepIndex])],
      }));
      
      setCurrentStep(stepIndex);
      setSavedStep(stepIndex);
      setStepStartTime(Date.now());
    }
  }, [setSavedStep, currentStep, stepStartTime, setTourAnalytics]);

  const tourState: TourState = {
    isActive,
    currentStep,
    isDismissed: tourDismissed,
    isCompleted: tourCompleted,
  };

  const value: TourContextValue = {
    tourState,
    steps: TOUR_STEPS,
    analytics: tourAnalytics,
    startTour,
    nextStep,
    previousStep,
    skipTour,
    finishTour,
    goToStep,
    showFeedbackModal,
    setShowFeedbackModal,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour() {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}
